import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddPaymentModeForm, { PaymentModeFormData } from './AddPaymentModeForm';
import { 
  getPaymentModeListAPI, 
  savePaymentModeAPI, 
  deletePaymentModeAPI,
  PaymentMode,
  PaymentModeSearchRequest 
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './PaymentModes.module.css';

const PaymentModes: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentModes, setPaymentModes] = useState<PaymentMode[]>([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [editingMode, setEditingMode] = useState<PaymentMode | null>(null);
  const permissions = useModulePermission('PaymentModes');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: PaymentModeSearchRequest = {
        searchText: '',
        pageNo: 1,
        pageSize: 100,
      };
      const response = await getPaymentModeListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setPaymentModes(response.data);
      } else {
        showError(response.message || 'Failed to load payment modes');
      }
    } catch (error) {
      console.error('Failed to load payment modes:', error);
      showError('Failed to load payment modes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMode = () => {
    setEditingMode(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMode(null);
  };

  const handleSubmit = async (formData: PaymentModeFormData) => {
    try {
      const modePayload: PaymentMode = {
        id: editingMode?.id,
        paymentModeName: formData.name,
        paymentTypeId: parseInt(formData.type) || 1,
        isActive: formData.status === 'Active' || formData.status === 'active',
      };

      const response = await savePaymentModeAPI(modePayload);
      
      if (response.isSuccess) {
        showSuccess(editingMode ? 'Payment mode updated successfully!' : 'Payment mode created successfully!');
        await loadData();
        setIsModalOpen(false);
        setEditingMode(null);
      } else {
        showError(response.message || 'Failed to save payment mode');
      }
    } catch (error) {
      console.error('Failed to save payment mode:', error);
      showError('Failed to save payment mode. Please try again.');
    }
  };

  const handleEdit = (mode: PaymentMode) => {
    setEditingMode(mode);
    setIsModalOpen(true);
  };

  const handleDelete = async (mode: PaymentMode) => {
    if (window.confirm(`Are you sure you want to delete ${mode.paymentModeName}?`)) {
      try {
        const response = await deletePaymentModeAPI(mode.id || 0);
        if (response.isSuccess) {
          await loadData();
          showSuccess('Payment mode deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete payment mode');
        }
      } catch (error) {
        console.error('Failed to delete payment mode:', error);
        showError('Failed to delete payment mode. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Payment Modes</h1>
          <p className={styles.subtitle}>Configure payment methods for transactions</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddPaymentMode}>
            + Add Payment Mode
          </button>
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading payment modes...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>NAME</th>
                <th>TYPE</th>
                <th>STATUS</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {paymentModes.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 4 : 3} className={styles.emptyState}>No payment modes found</td>
                </tr>
              ) : (
                paymentModes.map((mode) => (
                  <tr key={mode.id}>
                    <td className={styles.nameCell}>{mode.paymentModeName}</td>
                    <td>
                      <span className={styles.typeBadge}>{mode.paymentType || 'Cash'}</span>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[mode.isActive ? 'active' : 'inactive']}`}>
                        {mode.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button 
                              className={styles.actionIcon}
                              onClick={() => handleEdit(mode)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button 
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(mode)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingMode ? 'Edit Payment Mode' : 'Add New Payment Mode'}
      >
        <AddPaymentModeForm 
          onSubmit={handleSubmit} 
          onCancel={handleCloseModal}
          initialData={editingMode ? {
            name: editingMode.paymentModeName,
            type: editingMode.paymentTypeId?.toString() || '1',
            status: editingMode.isActive ? 'Active' : 'Inactive',
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default PaymentModes;
